import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import styled from 'styled-components';

const jsdiff = require('diff');

class DiffPopover extends Component {
    constructor(props) {
        super(props);
        this.toggle = this.toggle.bind(this);
        this.state = { open: false };
        this.componentID = 'ID' + DiffPopover._componentID++;
    }

    toggle() {
        this.setState(s => ({ open: !s.open }));
    }

    inputAreDifferent() {
        return typeof this.props.inputA === 'string' && this.props.inputA !== this.props.inputB;
    }

    render() {
        if (this.props.diffDisabled) return null;
        if (!this.inputAreDifferent()) return null;

        const diffFn = DiffPopover.fnMap[this.props.type];
        const parts  = diffFn(this.props.inputA || '', this.props.inputB || '', { ignoreWhitespace: true });

        return (
            <span>
                <DiffBadge onClick={this.toggle}>diff</DiffBadge>

                <Modal isOpen={this.state.open} toggle={this.toggle} size="lg">
                    <ModalHeader toggle={this.toggle} style={{ borderBottom: '1px solid #e2e8f0', fontSize: '0.88rem', fontWeight: 700, fontFamily: '"Inter", system-ui, sans-serif' }}>
                        {this.props.name} — differences
                    </ModalHeader>
                    <ModalBody style={{ padding: '16px' }}>
                        <DiffGrid>
                            <CompareHeader>
                                <CompareCol $before>
                                    <ColLabel $before>Recommended</ColLabel>
                                </CompareCol>
                                <CompareCol>
                                    <ColLabel>Current</ColLabel>
                                </CompareCol>
                            </CompareHeader>

                            <FieldCells>
                                <FieldCell $before>
                                    {parts.filter(p => !p.added).map((p, i) => (
                                        <Chunk key={i} $removed={p.removed}>{p.value}</Chunk>
                                    ))}
                                    {!this.props.inputA && <Muted>—</Muted>}
                                </FieldCell>
                                <FieldCell>
                                    {parts.filter(p => !p.removed).map((p, i) => (
                                        <Chunk key={i} $added={p.added}>{p.value}</Chunk>
                                    ))}
                                    {!this.props.inputB && <Muted>—</Muted>}
                                </FieldCell>
                            </FieldCells>
                        </DiffGrid>
                    </ModalBody>
                </Modal>
            </span>
        );
    }
}

DiffPopover._componentID = 0;

DiffPopover.fnMap = {
    chars:     jsdiff.diffChars,
    words:     jsdiff.diffWords,
    sentences: jsdiff.diffSentences,
    json:      jsdiff.diffJson,
};

DiffPopover.defaultProps = { inputA: '', inputB: '', type: 'words' };

DiffPopover.propTypes = {
    inputA: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    inputB: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    type:   PropTypes.oneOf(['chars', 'words', 'sentences', 'json']),
};

export default DiffPopover;

/* ── Styles ── */

const DiffBadge = styled.button`
    display: inline-flex;
    align-items: center;
    padding: 1px 7px;
    border-radius: 4px;
    border: 1px solid #fbbf24;
    background: #fef9c3;
    color: #92400e;
    font-size: 0.65rem;
    font-weight: 700;
    cursor: pointer;
    margin-right: 6px;
    vertical-align: middle;
    transition: background 120ms;
    &:hover { background: #fef08a; }
`;

const DiffGrid = styled.div`
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
`;

const CompareHeader = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    border-bottom: 2px solid #e2e8f0;
`;

const CompareCol = styled.div`
    padding: 10px 16px;
    background: ${({ $before }) => $before ? '#fff8f8' : '#f0fdf4'};
    border-right: ${({ $before }) => $before ? '1px solid #e2e8f0' : 'none'};
`;

const ColLabel = styled.div`
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${({ $before }) => $before ? '#dc2626' : '#15803d'};
`;

const FieldCells = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    min-height: 80px;
`;

const FieldCell = styled.div`
    padding: 16px;
    font-size: 0.82rem;
    color: #0f172a;
    line-height: 1.6;
    word-break: break-word;
    white-space: pre-wrap;
    font-family: "Inter", system-ui, sans-serif;
    background: ${({ $before }) => $before ? '#fff8f8' : '#f0fdf4'};
    border-right: ${({ $before }) => $before ? '1px solid #e2e8f0' : 'none'};
`;

const Chunk = styled.span`
    background: ${({ $added, $removed }) => $added ? '#bbf7d0' : $removed ? '#fecaca' : 'transparent'};
    ${({ $removed }) => $removed && 'text-decoration: line-through; opacity: 0.75;'}
    border-radius: 2px;
`;

const Muted = styled.span`
    color: #94a3b8;
    font-style: italic;
`;
